import { Mesh, Scene, Clock, Vector2, Raycaster } from "three";
import EventEmitter from "events";
import Debug from "./utils/debug";
import Size from "./utils/size";

import { Camera } from "./base/camera";
import Renderer from "./base/renderer/renderer";
import type { RendererConfig } from "./base/renderer/type";
import Loader from "./base/loader/loader";
import World from "./world/world";
import { Light } from "./base/light";
import { AvatarType } from "./avatar/common/player/type";

// 통합 config 타입 import
import { defaultConfig } from "./config/default.config";
import type { DefaultConfig } from "./config/default.config";

export interface ThreeJSAppOptions {
  canvas?: HTMLCanvasElement;
  size?: {
    width?: number;
    height?: number;
  };
  debug?: boolean;
  // 기존 개별 옵션들은 하위 호환성을 위해 유지
  loader?: {
    isKTX2?: boolean;
    isDRACO?: boolean;
    isMeshopt?: boolean;
  };
  renderer?: Partial<RendererConfig>;
  // 새로운 통합 config 옵션 추가
  config?: DefaultConfig;
}

export default class ThreeJSApp extends EventEmitter {
  canvas: HTMLCanvasElement;
  debug: Debug;
  size: Size;

  clock: Clock; // Three.js Clock 추가
  camera: Camera;
  renderer: Renderer;
  loader: Loader;
  scene: Scene;
  light: Light;
  world: World;

  // Raycast 관련 속성들
  private mouse: Vector2;
  private raycaster: Raycaster;
  private avatarClickCallback: ((avatarType: AvatarType | null) => void) | null = null;

  // 사용 중인 config 저장
  private appConfig: DefaultConfig;

  private _isDestroyed: boolean = false;
  private _needsUpdate: boolean = true; // 조건부 렌더링을 위한 플래그
  private targetFPS: number = 30; // 30FPS로 제한
  private interval: number;
  private lastTime: number = 0;

  constructor(options: ThreeJSAppOptions = {}) {
    super(); // EventEmitter 초기화

    // config 설정: 사용자가 제공하면 그것을 사용하고, 아니면 기본값 사용
    this.appConfig = options.config || defaultConfig;

    if (options.canvas) {
      this.canvas = options.canvas;
    } else {
      throw new Error("Canvas is required for ThreeJSApp");
    }

    // 각 인스턴스별로 독립적인 객체 생성
    this.debug = new Debug(options.debug);
    this.scene = new Scene();
    this.size = new Size(options.size || {
      width: this.canvas.clientWidth || this.canvas.width,
      height: this.canvas.clientHeight || this.canvas.height
    });

    this.camera = new Camera(this);
    this.renderer = new Renderer(this, this.getRendererConfig(options.renderer));
    this.clock = new Clock(); // Three.js Clock 초기화
    this.interval = 1000 / this.targetFPS; // 33.33ms 간격
    this.loader = new Loader({
      isKTX2: options.loader?.isKTX2 ?? this.appConfig.loader.options.isKTX2,
      isDRACO: options.loader?.isDRACO ?? this.appConfig.loader.options.isDRACO,
      isMeshopt: options.loader?.isMeshopt ?? this.appConfig.loader.options.isMeshopt,
    });
    this.loader.setRoot(this);
    this.light = new Light(this);

    // Raycast 초기화
    this.mouse = new Vector2();
    this.raycaster = new Raycaster();

    // Application Level
    this.world = new World(this);

    // Event & Callback 등록
    this.size.on("resize", () => {
      this.resize();
      this.markNeedsUpdate(); // 크기 변경 시 업데이트 필요
    });

    // 마우스 클릭 이벤트 등록
    this.canvas.addEventListener('click', this.onMouseClick.bind(this));

    // World의 loadProgress 이벤트를 받아서 상위로 전달
    this.world.on('loadProgress', (progressData) => {
      this.emit('loadProgress', progressData);
    });

    // setAnimationLoop로 애니메이션 시작
    this.startAnimationLoop();
  }

  // 렌더러 config 가져오기 (기존 옵션과 새로운 config 모두 지원)
  private getRendererConfig(legacyRendererOptions?: Partial<RendererConfig>): RendererConfig {
    if (legacyRendererOptions) {
      return { ...this.appConfig.renderer, ...legacyRendererOptions };
    }
    return this.appConfig.renderer;
  }

  // 현재 사용 중인 config 반환
  getConfig(): DefaultConfig {
    return this.appConfig;
  }

  // 동적으로 설정 업데이트
  updateConfig(newConfig: DefaultConfig) {

    this.appConfig = newConfig;

    // 각 모듈에 새로운 설정 적용
    this.camera.updateConfig(newConfig.camera);
    this.light.updateConfig(newConfig.lighting);
    this.renderer.updateConfig(newConfig.renderer);
    this.loader.updateOptions(newConfig.loader.options);

    // 업데이트 필요 표시 및 즉시 렌더링
    this.markNeedsUpdate();

    // 설정 변경 후 즉시 한 번 렌더링하여 품질 확인
    this.renderer.update();


  }

  // setAnimationLoop로 애니메이션 시작
  private startAnimationLoop() {
    this.renderer.instance.setAnimationLoop((time: number) => {
      if (this._isDestroyed) return;

      // 30FPS 제한
      const deltaTime = time - this.lastTime;
      if (deltaTime >= this.interval) {
        const delta = this.clock.getDelta();
        this.update(delta);
        this.lastTime = time - (deltaTime % this.interval);
      }
    });
  }

  // 업데이트 필요 표시
  markNeedsUpdate() {
    this._needsUpdate = true;
  }

  resize(width?: number, height?: number) {
    if (this._isDestroyed) return;

    // width, height가 제공되지 않으면 canvas 크기 기준으로 설정
    if (width === undefined || height === undefined) {
      const canvasWidth = this.canvas.clientWidth || this.canvas.width;
      const canvasHeight = this.canvas.clientHeight || this.canvas.height;

      // 직접 size 객체의 값을 업데이트하여 무한 재귀 호출 방지
      this.size.width = width ?? canvasWidth;
      this.size.height = height ?? canvasHeight;
    } else {
      // 직접 size 객체의 값을 업데이트하여 무한 재귀 호출 방지
      this.size.width = width;
      this.size.height = height;
    }

    this.camera.resize(this.size.width, this.size.height);
    this.renderer.resize(this.size.width, this.size.height);

    // 리사이즈 후 즉시 렌더링이 필요하므로 업데이트 플래그 설정
    this.markNeedsUpdate();
  }

  update(delta: number) {
    if (this._isDestroyed) return;

    // 월드 업데이트 (애니메이션 등)
    this.world.update(delta);

    // 조건부 렌더링 - 업데이트가 필요할 때만 렌더링
    if (this._needsUpdate) {
      this.renderer.update();
      this._needsUpdate = false;
    }
  }

  destroy() {
    if (this._isDestroyed) return;
    this._isDestroyed = true;

    // setAnimationLoop 중지
    this.renderer.instance.setAnimationLoop(null);

    this.size.off('resize', () => this.resize())

    // 마우스 이벤트 리스너 제거
    this.canvas.removeEventListener('click', this.onMouseClick.bind(this))


    // Traverse the whole scene
    this.scene.traverse((child) => {

      // Test if it's a mesh
      if (child instanceof Mesh) {

        child.geometry.dispose()

        // Loop through the material properties
        for (const key in child.material) {
          const value = child.material[key]

          // Test if there is a dispose function
          if (value && typeof value.dispose === 'function') {
            value.dispose()
          }
        }
      }
    })

    this.renderer.instance.dispose()
    this.light.destroy()
    this.world.destroy()


    if (this.debug.active) {
      this.debug.destroy()
    }
  }

  // World 재생성 메서드 (리셋 후 사용)
  recreateWorld(): void {


    // 기존 World가 있다면 정리
    if (this.world) {
      this.world.destroy();
    }

    // 새로운 World 인스턴스 생성
    this.world = new World(this);

    // World의 loadProgress 이벤트를 받아서 상위로 전달
    this.world.on('loadProgress', (progressData) => {
      this.emit('loadProgress', progressData);
    });


  }

  // React 컴포넌트에서 사용하기 위한 헬퍼 메서드들
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  getScene(): Scene {
    return this.scene;
  }

  isDestroyed(): boolean {
    return this._isDestroyed;
  }

  // 마우스 클릭 이벤트 핸들러
  private onMouseClick(event: MouseEvent): void {
    if (!this.avatarClickCallback || !this.world.isReady) {
      return;
    }

    // 마우스 좌표를 정규화된 좌표로 변환 (-1 ~ 1)
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Raycaster 설정
    this.raycaster.setFromCamera(this.mouse, this.camera.activeCamera);

    // 아바타 모델과의 교차 검사
    const currentAvatar = this.world.getCurrentAvatar();
    if (currentAvatar) {
      const avatarModel = currentAvatar.getModel();
      if (avatarModel) {
        const intersects = this.raycaster.intersectObject(avatarModel, true);

        if (intersects.length > 0) {
          // 아바타가 클릭되었을 때 콜백 실행
          const currentAvatarType = this.world.getCurrentAvatarType();
          this.avatarClickCallback(currentAvatarType);
        }
      }
    }
  }

  // 아바타 클릭 콜백 설정
  setAvatarClickCallback(callback: (avatarType: AvatarType | null) => void): void {
    this.avatarClickCallback = callback;
  }

  // 아바타 클릭 콜백 제거
  removeAvatarClickCallback(): void {
    this.avatarClickCallback = null;
  }
}

// 기존 Root 클래스를 위한 별칭 (하위 호환성)
export { ThreeJSApp as Root };